import React from 'react';
import { FormattedMessage } from 'react-intl';
import AreaChart from './AreaChart';
import ExportButton from './ExportButton';
import { OverviewData, RevenueMonthPoint } from '../hooks/useOverviewData';
import { downloadCsv, todayYmd } from '../utils/csv';

interface Props {
    data: OverviewData | null;
    loading: boolean;
    locale?: string;
}

const RevenueTrendCard: React.FC<Props> = ({ data, loading, locale = 'en' }) => {
    const handleExport = () => {
        if (!data || data.revenue_by_month.length === 0) return;
        const prevMap = new Map(
            (data.revenue_by_month_previous ?? []).map((p, i) => [i, p.revenue]),
        );
        downloadCsv<RevenueMonthPoint & { _index: number }>(
            `revenue-trend-${todayYmd()}.csv`,
            [
                { header: 'Month', accessor: (r) => r.month },
                { header: 'Revenue', accessor: (r) => r.revenue },
                { header: 'Revenue (previous period)', accessor: (r) => prevMap.get(r._index) ?? '' },
                { header: 'Agreements', accessor: (r) => r.agreement_count },
            ],
            data.revenue_by_month.map((p, i) => ({ ...p, _index: i })),
        );
    };

    return (
        <div className="av2-card">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">
                        <FormattedMessage id="analyticsV2.revenue.title" defaultMessage="Revenue" />
                    </h3>
                    <p className="av2-card__subtitle">
                        <FormattedMessage
                            id="analyticsV2.revenue.subtitle"
                            defaultMessage="Monthly trend, with the previous period overlay"
                        />
                    </p>
                </div>
                <ExportButton
                    onClick={handleExport}
                    disabled={!data || data.revenue_by_month.length === 0}
                />
            </div>
            <div className="av2-card__body">
                {loading || !data ? (
                    <div className="av2-skel av2-skel--chart" />
                ) : data.revenue_by_month.length === 0 ? (
                    <div className="av2-empty">
                        <FormattedMessage
                            id="analyticsV2.empty.noRevenue"
                            defaultMessage="No revenue in this period yet."
                        />
                    </div>
                ) : (
                    <AreaChart
                        data={data.revenue_by_month}
                        previousData={data.revenue_by_month_previous}
                        locale={locale}
                    />
                )}
            </div>
        </div>
    );
};

export default RevenueTrendCard;
